/**
 * Settlement detail — the heart of the product.
 *
 * Layout:
 *   1. Header: title, period, status badge, actions
 *   2. Status banner (overdue / awaiting approval)
 *   3. Invoices section (collapsible)
 *   4. Expenses section (the main content)
 *      - Filter bar: shared / private / manual / by source
 *      - Table with: dato, beskrivelse, kategori, beløp, type, kilde
 *      - Per-row: edit, delete, toggle shared
 *      - Add manual expense button
 *   5. Charts: spending by category + per member
 *   6. Settlement summary: who owes whom + per-member approval
 */

import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { t } from "../i18n";
import { formatPeriod } from "../lib/format";
import { formatDueRelative, getSettlementStatus } from "../lib/settlementStatus";
import { trpc } from "../lib/trpc";
import { HouseholdBreadcrumb } from "./HouseholdBreadcrumb";
import {
  Badge,
  Card,
  DropdownMenu,
  EmptyState,
  IconButton,
  MenuItem,
  PageHeader,
  Skeleton,
  SkeletonBreadcrumb,
  SkeletonList,
  SkeletonTable,
  Stack,
  useToast,
} from "./ui";

export interface SettlementDetailProps {
  householdId: string;
  settlementId: string;
}

// Re-export shared types for backwards-compat with anything that still
// imports them directly from SettlementDetail. Internal sub-components
// in `./settlement/` should import from `./settlement/types` directly.
export type {
  Expense,
  ExpenseCategory,
  ExpenseFilter,
  Member,
  MemberFilter,
  ResolvePayerState,
  Settlement,
  SettlementCategory,
  SettlementEvent,
  SmartSuggestion,
  SortDirection,
  SortKey,
} from "./settlement/types";

import { DeleteSettlementDialog } from "./settlement/dialogs/DeleteSettlementDialog";
import { EditSettlementDialog } from "./settlement/dialogs/EditSettlementDialog";
import { CardSourcesSection } from "./settlement/sections/CardSourcesSection";
import { ChartsSection } from "./settlement/sections/ChartsSection";
import { ExpensesSection } from "./settlement/sections/ExpensesSection";
import { InvoicesSection } from "./settlement/sections/InvoicesSection";
import { SummarySection } from "./settlement/sections/SummarySection";
import type { Member, Settlement, SettlementCategory } from "./settlement/types";

export default function SettlementDetail({ householdId, settlementId }: SettlementDetailProps) {
  // Realtime subscription is owned by `<AppShell>` (one WebSocket per
  // tab via `useUserConnection`). The invalidate-on-event predicate
  // already covers `trpc.settlement.*` queries, so no per-page hook is
  // needed.

  const navigate = useNavigate();
  const onBack = () =>
    navigate({
      to: "/households/$householdId/$tab",
      params: { householdId, tab: "settlements" },
    });
  const settlement = trpc.settlement.get.useQuery({ settlementId });

  // Need household members for "who paid" / "splits" — load alongside.
  const household = trpc.household.get.useQuery(
    { groupId: settlement.data?.householdId ?? "" },
    { enabled: !!settlement.data },
  );

  const { show } = useToast();
  const settlementError = settlement.isError ? settlement.error.data?.code : null;
  const householdError = household.isError ? household.error.data?.code : null;
  const accessDenied =
    settlementError === "FORBIDDEN" ||
    settlementError === "NOT_FOUND" ||
    householdError === "FORBIDDEN" ||
    householdError === "NOT_FOUND";
  useEffect(() => {
    if (!accessDenied) return;
    show({
      tone: "warning",
      title: t.household.accessRevokedToast,
      dismissLabel: t.invitations.toast.dismissLabel,
    });
    navigate({ to: "/" });
  }, [accessDenied, navigate, show]);

  if (settlement.isPending) {
    return (
      <output aria-busy="true" className="block">
        <span className="sr-only">{t.common.loading}</span>
        <PageHeader
          eyebrow={<SkeletonBreadcrumb />}
          title={<Skeleton className="h-6" style={{ width: "16rem" }} />}
          description={<Skeleton className="mt-1 h-4" style={{ width: "20rem" }} />}
        />
        <Stack gap={6}>
          <Card>
            <Stack gap={3}>
              <Skeleton className="h-5" style={{ width: "8rem" }} />
              <SkeletonList rows={3} />
            </Stack>
          </Card>
          <Card>
            <Stack gap={3}>
              <Skeleton className="h-5" style={{ width: "8rem" }} />
              <div className="hidden sm:block">
                <SkeletonTable rows={6} columns={5} />
              </div>
              <div className="sm:hidden">
                <SkeletonList rows={6} />
              </div>
            </Stack>
          </Card>
          <Card>
            <Stack gap={3}>
              <Skeleton className="h-5" style={{ width: "8rem" }} />
              <div className="grid gap-3 sm:grid-cols-2">
                <Skeleton className="h-48" />
                <Skeleton className="h-48" />
              </div>
            </Stack>
          </Card>
        </Stack>
      </output>
    );
  }
  if (settlement.isError || !settlement.data) {
    return (
      <EmptyState
        icon="alert-circle"
        title={t.settlement.loadFailed}
        description={settlement.error?.message}
      />
    );
  }

  const data = settlement.data;
  const members: Member[] =
    household.data?.members.map((m) => ({
      userId: m.userId,
      email: m.email,
      displayName: m.displayName,
    })) ?? [];
  const categories = household.data?.categories ?? [];

  return (
    <SettlementContent
      settlement={data}
      householdId={householdId}
      householdName={household.data?.name ?? ""}
      ownerCoversInvoice={household.data?.ownerCoversInvoice ?? true}
      householdRole={household.data?.role ?? null}
      members={members}
      categories={categories}
      onBack={onBack}
    />
  );
}

function SettlementContent({
  settlement,
  householdId,
  householdName,
  ownerCoversInvoice,
  householdRole,
  members,
  categories,
  onBack,
}: {
  settlement: Settlement;
  householdId: string;
  householdName: string;
  ownerCoversInvoice: boolean;
  householdRole: "owner" | "member" | null;
  members: Member[];
  categories: SettlementCategory[];
  onBack: () => void;
}) {
  const statusInfo = getSettlementStatus({
    status: settlement.status,
    endDate: settlement.endDate,
  });
  const isLocked = statusInfo.status === "locked";
  const showDueHint = !isLocked && statusInfo.status !== "awaiting_approval";
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  return (
    <>
      <PageHeader
        eyebrow={
          householdName ? (
            <HouseholdBreadcrumb
              currentHouseholdId={householdId}
              currentHouseholdName={householdName}
            />
          ) : (
            <SkeletonBreadcrumb />
          )
        }
        title={
          <span className="flex items-center gap-2">
            <span>{settlement.title}</span>
            <Badge tone={statusInfo.tone} iconLeft={statusInfo.icon}>
              {statusInfo.label}
            </Badge>
          </span>
        }
        description={
          <>
            {formatPeriod(settlement.startDate, settlement.endDate)}
            {showDueHint && (
              <>
                {" · "}
                <span
                  className={
                    statusInfo.status === "overdue"
                      ? "font-medium text-rose-600 dark:text-rose-400"
                      : undefined
                  }
                >
                  {formatDueRelative(settlement.endDate)}
                </span>
              </>
            )}
          </>
        }
        actions={
          !isLocked ? (
            <DropdownMenu
              trigger={
                <IconButton icon="more-vertical" label={t.common.more} variant="secondary" />
              }
            >
              <MenuItem icon="edit" onClick={() => setEditOpen(true)}>
                {t.settlement.actions.edit}
              </MenuItem>
              <MenuItem icon="trash" destructive onClick={() => setDeleteOpen(true)}>
                {t.settlement.actions.delete}
              </MenuItem>
            </DropdownMenu>
          ) : null
        }
      />

      <EditSettlementDialog
        settlement={settlement}
        open={editOpen}
        onClose={() => setEditOpen(false)}
      />

      <DeleteSettlementDialog
        settlement={settlement}
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onDeleted={onBack}
      />

      <Stack gap={6}>
        <InvoicesSection
          settlementId={settlement.id}
          invoices={settlement.invoices}
          expenses={settlement.expenses}
          members={members}
          categories={categories}
          currency={settlement.currency}
          isLocked={isLocked}
        />

        <CardSourcesSection
          settlementId={settlement.id}
          householdId={householdId}
          isLocked={isLocked}
        />

        <ExpensesSection
          settlementId={settlement.id}
          householdId={householdId}
          expenses={settlement.expenses}
          invoices={settlement.invoices}
          members={members}
          categories={categories}
          currency={settlement.currency}
          isLocked={isLocked}
        />

        <ChartsSection
          expenses={settlement.expenses}
          categories={categories}
          members={members}
          currency={settlement.currency}
        />

        <SummarySection
          settlement={settlement}
          members={members}
          isLocked={isLocked}
          ownerCoversInvoice={ownerCoversInvoice}
          householdRole={householdRole}
        />
      </Stack>
    </>
  );
}
